/**
 * Workspace "Do Not Call registry" card.
 * Props: api (API.json), toast(message, type).
 * Mount from Settings for tenant_admin (and a super admin using View as).
 */
const { useEffect, useState } = React;

const FTC_URL = 'https://telemarketing.donotcall.gov';
const ATTEST_LABEL = 'I confirm this business holds its own active FTC SAN that covers the area codes we call. Sharing a SAN with another company is not allowed.';
const FEDERAL_ONLY_NOTE = 'This checks the National Do Not Call Registry. State lists are not included.';

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function badgeFor(profile) {
  const status = profile && profile.validation_status;
  if (!profile || !profile.ftc_san || status === 'not_started' || !status) {
    return { kind: 'neutral', text: 'Not set up' };
  }
  if (status === 'pending') return { kind: 'pending', text: 'Checking with the FTC' };
  if (status === 'validated') {
    const expires = profile.san_expires_on ? ' – expires ' + profile.san_expires_on : '';
    if (profile.entire_us) return { kind: 'ok', text: 'Confirmed – entire US' + expires };
    const areas = profile.area_codes && profile.area_codes.length
      ? profile.area_codes.join(', ')
      : 'subscribed area codes';
    return { kind: 'ok', text: 'Confirmed – area codes ' + areas + expires };
  }
  if (status === 'self_attested') {
    const expires = profile.san_expires_on ? String(profile.san_expires_on).slice(0, 10) : '';
    if (expires && expires < todayInputValue()) {
      return { kind: 'bad', text: 'Problem: This SAN has expired.' };
    }
    return { kind: 'ok', text: 'Confirmed by your admin – expires ' + expires };
  }
  const problem = profile.validation_message
    || (status === 'expired' ? 'This SAN has expired.' : 'Check the Org ID and SAN.');
  return { kind: 'bad', text: 'Problem: ' + problem };
}

function badgeStyle(kind) {
  const colors = {
    neutral: { background: '#eef2f6', color: '#243042' },
    pending: { background: '#fff6e5', color: '#8a5a00' },
    ok: { background: '#e7f6ee', color: '#0f6b3c' },
    bad: { background: '#fde8e8', color: '#9b1c1c' }
  };
  const tone = colors[kind] || colors.neutral;
  return {
    display: 'inline-block',
    padding: '0.2rem 0.55rem',
    borderRadius: 999,
    fontSize: '0.85rem',
    fontWeight: 700,
    background: tone.background,
    color: tone.color
  };
}

function checksUsedText(allowance) {
  const used = Number(allowance && allowance.scrubs_used) || 0;
  if (allowance && allowance.scrub_limit == null) return 'Checks used: ' + used + ' (no limit)';
  const limit = Number(allowance && allowance.scrub_limit) || 0;
  const remaining = allowance && allowance.remaining != null
    ? Number(allowance.remaining)
    : Math.max(0, limit - used);
  return 'Checks used: ' + used + ' of ' + limit + '. Remaining: ' + remaining + '.';
}

function todayInputValue() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return now.getFullYear() + '-' + month + '-' + day;
}

export function DncComplianceSettings({ api, toast }) {
  const [profile, setProfile] = useState(null);
  const [needsAttestation, setNeedsAttestation] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [orgId, setOrgId] = useState('');
  const [san, setSan] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [attest, setAttest] = useState(false);
  const [provider, setProvider] = useState('');
  const [allowance, setAllowance] = useState(null);
  const [checkPhone, setCheckPhone] = useState('');
  const [checkResult, setCheckResult] = useState(null);

  function apply(data) {
    const next = data && data.profile ? data.profile : null;
    setProfile(next);
    setOrgId(next && next.ftc_org_id ? next.ftc_org_id : '');
    setSan(next && next.ftc_san ? next.ftc_san : '');
    setExpiresOn(next && next.san_expires_on ? String(next.san_expires_on).slice(0, 10) : '');
    if (data && typeof data.needs_attestation === 'boolean') setNeedsAttestation(data.needs_attestation);
    if (data && data.coverage && typeof data.coverage === 'object') setCoverage(data.coverage);
    if (data && typeof data.provider === 'string') setProvider(data.provider);
    if (data && data.allowance) setAllowance(data.allowance);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api('GET', '/api/compliance/dnc-profile').then((data) => {
      if (!cancelled) apply(data);
    }).catch((err) => {
      if (!cancelled) setError(errorMessage(err, 'Could not load Do Not Call settings.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  function notify(message, type) {
    if (toast) toast(message, type);
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    const payload = {
      ftc_org_id: orgId.trim(),
      ftc_san: san.trim()
    };
    if (needsAttestation) {
      payload.san_expires_on = expiresOn;
      payload.attest = attest === true;
    }
    try {
      const data = await api('PUT', '/api/compliance/dnc-profile', payload);
      apply(data);
      notify('Do Not Call details saved.');
    } catch (err) {
      const message = errorMessage(err, 'Could not save the Org ID and SAN.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function refresh() {
    setBusy(true);
    setError('');
    try {
      const data = await api('POST', '/api/compliance/dnc-profile/refresh', {});
      apply(data);
      notify(data && data.message ? data.message : 'FTC status updated.');
    } catch (err) {
      const message = errorMessage(err, 'Could not refresh FTC status.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function checkNumber(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setCheckResult(null);
    try {
      const data = await api('POST', '/api/compliance/dnc-check', { phone: checkPhone.trim() });
      setCheckResult(data || null);
      if (data && data.allowance) setAllowance(data.allowance);
    } catch (err) {
      const message = errorMessage(err, 'Could not check that number.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <section>
        <h3 className="section-heading">Do Not Call registry</h3>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (provider === 'donotcalldnc') {
    const turnedOn = allowance && (allowance.mode === 'auto' || allowance.mode === 'manual');
    return (
      <section>
        <h3 className="section-heading">Do Not Call registry</h3>
        {coverage && coverage.state === false ? <p className="muted">{FEDERAL_ONLY_NOTE}</p> : null}
        {error ? <p className="field-help" role="alert">{error}</p> : null}
        {!turnedOn ? (
          <p className="field-help">Do Not Call checking has not been turned on for this workspace.</p>
        ) : (
          <>
            <p className="field-help">{checksUsedText(allowance)}</p>
            {allowance.mode === 'auto' ? (
              <p className="field-help">Numbers are checked automatically before a call.</p>
            ) : (
              <p className="field-help">Checks run only when you use Check a number. Calls are not checked automatically.</p>
            )}
            <form onSubmit={checkNumber}>
              <div className="field">
                <label htmlFor="dnc-check-phone">Phone number</label>
                <input
                  id="dnc-check-phone"
                  className="input"
                  type="tel"
                  value={checkPhone}
                  autoComplete="off"
                  disabled={busy}
                  onChange={(event) => setCheckPhone(event.target.value)}
                  placeholder="925-404-7311"
                />
              </div>
              <button className="button primary small" type="submit" disabled={busy || !checkPhone.trim()}>
                {busy ? 'Checking…' : 'Check a number'}
              </button>
            </form>
            {checkResult ? (
              <p role="status">
                {checkResult.listed ? 'On the National Do Not Call Registry' : 'Not on the National Do Not Call Registry'}
                {checkResult.cached ? ' Used a saved result.' : ''}
              </p>
            ) : null}
          </>
        )}
      </section>
    );
  }

  const badge = badgeFor(profile);
  const canRefresh = !!(profile && profile.ftc_san);
  const attestationReady = !needsAttestation || (attest && expiresOn);

  return (
    <section>
      <h3 className="section-heading">Do Not Call registry</h3>
      <p className="field-help">
        Every business that makes sales calls needs its own free FTC account number (SAN). The first 5 area codes are free; extra area codes cost $82 per year each (FY2026). Get yours at{' '}
        <a href={FTC_URL} target="_blank" rel="noopener noreferrer">telemarketing.donotcall.gov</a>
        , then paste your Org ID and SAN here.
      </p>
      {coverage && coverage.state === false ? <p className="muted">{FEDERAL_ONLY_NOTE}</p> : null}
      {error ? <p className="field-help" role="alert">{error}</p> : null}
      <p>
        <span style={badgeStyle(badge.kind)} role="status">{badge.text}</span>
      </p>
      {profile && profile.next_step ? <p className="field-help">{profile.next_step}</p> : null}
      <form onSubmit={save}>
        <div className="field">
          <label htmlFor="dnc-ftc-org-id">FTC Org ID</label>
          <input
            id="dnc-ftc-org-id"
            className="input"
            value={orgId}
            autoComplete="off"
            disabled={busy}
            onChange={(event) => setOrgId(event.target.value)}
            placeholder="10000598-10598"
          />
        </div>
        <div className="field">
          <label htmlFor="dnc-ftc-san">FTC SAN</label>
          <input
            id="dnc-ftc-san"
            className="input"
            value={san}
            autoComplete="off"
            disabled={busy}
            onChange={(event) => setSan(event.target.value)}
            placeholder="10000830-100830-07"
          />
        </div>
        {needsAttestation ? (
          <div className="field">
            <label htmlFor="dnc-san-expires">SAN expiration date</label>
            <input
              id="dnc-san-expires"
              className="input"
              type="date"
              value={expiresOn}
              min={todayInputValue()}
              required
              autoComplete="off"
              disabled={busy}
              onChange={(event) => setExpiresOn(event.target.value)}
            />
          </div>
        ) : null}
        {needsAttestation ? (
          <div className="field">
            <label htmlFor="dnc-san-attest">
              <input
                id="dnc-san-attest"
                type="checkbox"
                checked={attest}
                required
                disabled={busy}
                onChange={(event) => setAttest(event.target.checked)}
              />
              {' '}
              {ATTEST_LABEL}
            </label>
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="button primary small" type="submit" disabled={busy || !orgId.trim() || !san.trim() || !attestationReady}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="button small" type="button" disabled={busy || !canRefresh} onClick={refresh}>
            Refresh
          </button>
        </div>
      </form>
    </section>
  );
}
